import {Injectable} from "@angular/core";
import {SwUpdate, VersionReadyEvent} from "@angular/service-worker";
import {BehaviorSubject, Observable} from "rxjs";
import {filter} from "rxjs/operators";

/**
 * Detects when a new frontend build is deployed and drives the "update" popup.
 *
 * The app ships the Angular service worker (see app.module + ngsw-config.json),
 * which caches all JS/CSS aggressively — that's why HTTP polling / Ctrl+F5 are
 * unreliable. Instead we use the service worker's own update mechanism:
 *
 *   1. checkForUpdate() asks the SW to fetch a fresh ngsw.json manifest
 *      (cache-busted by the SW) and download any new build in the background.
 *   2. When that download is fully cached, `versionUpdates` fires VERSION_READY
 *      and we reveal the popup.
 *   3. activateAndReload() atomically swaps to the new build and reloads —
 *      this GUARANTEES the user is on the new version (no Ctrl+F5 needed).
 *
 * The Pusher deploy broadcast (PusherService) just calls check() so the update
 * is picked up instantly instead of waiting for the poll interval.
 */
@Injectable({providedIn: "root"})
export class UpdateCheckService {

    /** Fallback poll interval, in case the websocket nudge is missed. */
    private readonly pollIntervalMs: number = 20 * 60 * 1000;

    private visibleSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public visible$: Observable<boolean> = this.visibleSubject.asObservable();

    private timer: any = null;

    public constructor(private swUpdate: SwUpdate) {
    }

    /** Called once from the root component. */
    public init(): void {
        // Disabled in dev (no service worker) — nothing to do.
        if (!this.swUpdate.isEnabled) {
            return;
        }

        // A new build has been fully downloaded and is ready to activate.
        this.swUpdate.versionUpdates
            .pipe(filter((event): event is VersionReadyEvent => event.type === "VERSION_READY"))
            .subscribe((): void => this.visibleSubject.next(true));

        // Check right away, then poll, and again whenever the tab regains focus.
        this.check();
        this.timer = setInterval((): void => this.check(), this.pollIntervalMs);
        document.addEventListener("visibilitychange", (): void => {
            if (document.visibilityState === "visible") {
                this.check();
            }
        });
    }

    /** Ask the service worker to look for a new build now. Safe to call any time. */
    public check(): void {
        if (!this.swUpdate.isEnabled) {
            return;
        }
        this.swUpdate.checkForUpdate().catch((): void => {
            // Network hiccup — the next poll/nudge will retry.
        });
    }

    /** Atomically switch to the downloaded build and reload. Guarantees the new version. */
    public async activateAndReload(): Promise<void> {
        try {
            if (this.swUpdate.isEnabled) {
                await this.swUpdate.activateUpdate();
            }
        } finally {
            document.location.reload();
        }
    }

    public dismiss(): void {
        this.visibleSubject.next(false);
    }
}